import { cn } from '@neo/lib/utils'
import { AnimatePresence, m } from 'framer-motion'
import { ArrowRight, Check, ChevronDown, Circle } from 'lucide-react'
import { DURATION, EASE_OUT } from '../motion'
import { useVibeAgentStore } from '../store'

/**
 * Checklist tracker, Codex-informed: collapsed = donut ring + "Step n / N" +
 * the active step's title; expanded = numbered rows, sized to fit every step
 * (no inner scroll area — the transcript scrolls, the card doesn't). On
 * completion the header flips to past tense with the run duration (FE-local
 * timing — reloads show the plain completed state, never a fabricated
 * duration).
 */

const formatDuration = (ms: number): string => {
  const total = Math.max(1, Math.round(ms / 1000))
  const min = Math.floor(total / 60)
  const sec = total % 60
  if (min === 0) return `${sec}s`
  return sec === 0 ? `${min}m` : `${min}m ${sec}s`
}

/** Codex-style progress ring: pathLength normalizes the arc to 100 units, so
 *  dashoffset = 100 - percent. Track at 16% opacity; arc fills clockwise from
 *  12 o'clock. Transition lives in .vibe-donut-arc (style.scss) so
 *  prefers-reduced-motion can kill it. */
const ProgressDonut = ({ percent }: { percent: number }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
    className="shrink-0 -rotate-90 text-foreground"
    aria-hidden="true"
  >
    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeOpacity="0.16" strokeWidth="2.5" />
    <circle
      cx="8"
      cy="8"
      r="6.5"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      pathLength={100}
      strokeDasharray={100}
      strokeDashoffset={100 - percent}
      className="vibe-donut-arc"
      opacity={percent === 0 ? 0 : 1}
    />
  </svg>
)

export const VibeProgressWidget = () => {
  const checklist = useVibeAgentStore((state) => state.checklist)
  const explanation = useVibeAgentStore((state) => state.checklistExplanation)
  const isExpanded = useVibeAgentStore((state) => state.checklistExpanded)
  const setChecklistExpanded = useVibeAgentStore((state) => state.setChecklistExpanded)
  const runStartedAt = useVibeAgentStore((state) => state.runStartedAt)
  const runEndedAt = useVibeAgentStore((state) => state.runEndedAt)

  const completedCount = checklist.filter((item) => item.status === 'completed').length
  const totalCount = checklist.length
  const isComplete = completedCount === totalCount && totalCount > 0
  const progressPercent = isComplete
    ? 100
    : totalCount > 0
      ? (completedCount / totalCount) * 100
      : 0

  // Codex step-number rule: first in_progress, else first not-completed, else last.
  const inProgressIdx = checklist.findIndex((i) => i.status === 'in_progress')
  const firstOpenIdx = checklist.findIndex((i) => i.status !== 'completed')
  const activeIdx =
    inProgressIdx >= 0 ? inProgressIdx : firstOpenIdx >= 0 ? firstOpenIdx : totalCount - 1
  const activeStep = checklist[activeIdx]

  if (checklist.length === 0) return null

  const duration =
    isComplete && runStartedAt && runEndedAt && runEndedAt > runStartedAt
      ? formatDuration(runEndedAt - runStartedAt)
      : null

  const toggleExpanded = () => {
    setChecklistExpanded(!isExpanded)
  }

  return (
    <m.div
      initial={{ opacity: 0, transform: 'translateY(10px)' }}
      animate={{ opacity: 1, transform: 'translateY(0px)' }}
      transition={{ duration: DURATION.mount, ease: EASE_OUT }}
      className="mb-2"
    >
      <div className="border border-st2 bg-bg2 rounded-2xl p-3">
        {/* Whole header row is the toggle — the title is the target, not a
            small chevron. */}
        <button
          type="button"
          onClick={toggleExpanded}
          className="w-full flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity min-w-0"
          data-testid="button-toggle-progress-widget"
          aria-expanded={isExpanded}
          aria-label={isExpanded ? 'Collapse progress tracker' : 'Expand progress tracker'}
        >
          <ProgressDonut percent={progressPercent} />
          {isComplete ? (
            <span className="text-p-500 whitespace-nowrap">
              All {totalCount} tasks completed
              {duration && <span className="text-secondary text-s-400"> · in {duration}</span>}
            </span>
          ) : (
            <span className="text-p-500 whitespace-nowrap tabular-nums">
              Step {Math.min(activeIdx + 1, totalCount)} / {totalCount}
            </span>
          )}
          {/* Collapsed: the current step rides in the header so collapsing
              never means losing sight of what the agent is doing. */}
          {!isExpanded && !isComplete && activeStep && (
            <span className="flex-1 min-w-0 truncate text-left text-p-400 text-secondary">
              {activeStep.step}
            </span>
          )}
          {(isExpanded || isComplete || !activeStep) && <span className="flex-1" />}
          <m.div
            initial={false}
            animate={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
            transition={{ duration: DURATION.minor, ease: EASE_OUT }}
          >
            <ChevronDown className="w-4 h-4 text-secondary flex-shrink-0" />
          </m.div>
        </button>

        {/* Expanded content */}
        <AnimatePresence initial={false}>
          {isExpanded && (
            <m.div
              key="checklist-content"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: DURATION.panel, ease: EASE_OUT }}
              style={{ overflow: 'hidden' }}
            >
              {explanation && (
                <div className="text-p-400 text-secondary mb-2 whitespace-pre-wrap mt-2">
                  {explanation}
                </div>
              )}
              <div className="mt-2">
                <ul className="list-none p-0 m-0">
                  {checklist.map((item, idx) => {
                    const isCompleted = item.status === 'completed'
                    const isInProgress = item.status === 'in_progress'

                    return (
                      <li
                        key={item.id || idx}
                        id={`plan-item-${idx}`}
                        className="flex items-start gap-2 py-1"
                      >
                        <div className="mt-0.5">
                          {isCompleted ? (
                            <div className="w-4 h-4 rounded-full flex items-center justify-center border border-secondary">
                              <Check className="w-3 h-3 text-secondary" />
                            </div>
                          ) : isInProgress ? (
                            <div className="w-4 h-4 rounded-full flex items-center justify-center border border-primary bg-primary">
                              <ArrowRight className="w-3 h-3 text-primary-foreground" />
                            </div>
                          ) : (
                            <Circle className="w-4 h-4 text-secondary" />
                          )}
                        </div>
                        <span className="text-s-400 text-secondary/70 tabular-nums pt-[1px] shrink-0">
                          {idx + 1}.
                        </span>
                        <span
                          className={cn(
                            'text-p-400',
                            isCompleted && 'line-through text-secondary',
                            isInProgress ? 'text-p-500 text-foreground' : 'text-secondary',
                          )}
                        >
                          {item.step}
                        </span>
                      </li>
                    )
                  })}
                </ul>
              </div>
            </m.div>
          )}
        </AnimatePresence>
      </div>
    </m.div>
  )
}
